/**
 * Offcanvas Mobile Navigation
 * Handles mobile mega menu with accordion-style interactions
 */

const CLASSES = {
    open: 'is-open',
};

const SELECTORS = {
    root: '[data-offcanvas-nav]',
    item: '[data-offcanvas-mega]',
    trigger: '[data-offcanvas-mega-trigger]',
    panel: '[data-offcanvas-mega-panel]',
    section: '[data-offcanvas-section]',
    sectionTrigger: '[data-offcanvas-section-trigger]',
    sectionPanel: '[data-offcanvas-section-panel]',
};

/**
 * Set panel height using CSS custom property
 */
const setPanelHeight = (panel, variableName, height = null) => {
    const finalHeight = height !== null ? height : panel.scrollHeight;
    panel.style.setProperty(variableName, `${finalHeight}px`);
};

/**
 * Schedule height update for next animation frame
 */
const scheduleHeightUpdate = (callback) => {
    requestAnimationFrame(callback);
};

/**
 * Set panel accessibility state
 */
const setPanelState = (panel, isOpen) => {
    if (isOpen) {
        panel.removeAttribute('inert');
        panel.setAttribute('aria-hidden', 'false');
    } else {
        panel.setAttribute('inert', '');
        panel.setAttribute('aria-hidden', 'true');
    }
};

/**
 * Get desired panel height including open sections
 */
const getDesiredPanelHeight = (panel) => {
    let height = 0;

    // Get base panel height from inner content
    const inner = panel.querySelector('.offcanvas__mega-inner');
    if (inner) {
        // Get all direct children height
        const children = Array.from(inner.children);
        children.forEach((child) => {
            // Check if this child is a section
            const isSection = child.matches(SELECTORS.section);

            if (isSection) {
                // Add section trigger height
                const trigger = child.querySelector(SELECTORS.sectionTrigger);
                if (trigger) {
                    height += trigger.offsetHeight;
                }

                // Add section panel height if open
                if (child.classList.contains(CLASSES.open)) {
                    const sectionPanel = child.querySelector(SELECTORS.sectionPanel);
                    if (sectionPanel) {
                        height += sectionPanel.scrollHeight;
                    }
                }
            } else {
                // Add height of non-section elements (like simple links)
                height += child.offsetHeight;
            }
        });

        // Add inner padding and gap
        const computedStyle = window.getComputedStyle(inner);
        height += parseFloat(computedStyle.paddingTop) + parseFloat(computedStyle.paddingBottom);

        // Add gap between children
        const gap = parseFloat(computedStyle.gap) || parseFloat(computedStyle.rowGap) || 0;
        if (children.length > 1) {
            height += gap * (children.length - 1);
        }
    }

    return height;
};

/**
 * Initialize Offcanvas Navigation
 */
export const OffcanvasNav = () => {
    const root = document.querySelector(SELECTORS.root);

    if (!root) {
        console.warn('OffcanvasNav: Navigation element not found');

        return;
    }

    const items = Array.from(root.querySelectorAll(SELECTORS.item));

    if (!items.length) {
        console.warn('OffcanvasNav: No mega menu items found');

        return;
    }

    /**
     * Update item panel height
     */
    const updateItemPanelHeight = (item) => {
        // Panel is the next sibling of the item (heading)
        const panel = item.nextElementSibling;

        if (!panel || !panel.matches(SELECTORS.panel)) {
            console.warn('Panel not found as next sibling', item);

            return;
        }

        scheduleHeightUpdate(() => {
            const desiredHeight = getDesiredPanelHeight(panel);
            setPanelHeight(panel, '--offcanvas-mega-height', desiredHeight);
        });
    };

    /**
     * Close section
     */
    const closeSection = (section) => {
        const trigger = section.querySelector(SELECTORS.sectionTrigger);
        const panel = section.querySelector(SELECTORS.sectionPanel);

        if (!trigger || !panel) return;

        section.classList.remove(CLASSES.open);
        trigger.setAttribute('aria-expanded', 'false');
        setPanelState(panel, false);

        // Update parent item height - find the heading that contains the trigger
        const parentPanel = section.closest(SELECTORS.panel);
        if (parentPanel) {
            const item = parentPanel.previousElementSibling;
            if (item && item.matches(SELECTORS.item)) {
                updateItemPanelHeight(item);
            }
        }
    };

    /**
     * Open section
     */
    const openSection = (section) => {
        const trigger = section.querySelector(SELECTORS.sectionTrigger);
        const panel = section.querySelector(SELECTORS.sectionPanel);

        if (!trigger || !panel) return;

        section.classList.add(CLASSES.open);
        trigger.setAttribute('aria-expanded', 'true');
        setPanelState(panel, true);

        scheduleHeightUpdate(() => {
            setPanelHeight(panel, '--offcanvas-section-height');

            // Update parent item height
            const parentPanel = section.closest(SELECTORS.panel);
            if (parentPanel) {
                const item = parentPanel.previousElementSibling;
                if (item && item.matches(SELECTORS.item)) {
                    updateItemPanelHeight(item);
                }
            }
        });
    };

    /**
     * Close item
     */
    const closeItem = (item) => {
        const trigger = item.querySelector(SELECTORS.trigger);
        const panel = item.nextElementSibling;

        if (!trigger || !panel || !panel.matches(SELECTORS.panel)) return;

        item.classList.remove(CLASSES.open);
        trigger.setAttribute('aria-expanded', 'false');
        setPanelState(panel, false);

        // Close all sections
        const sections = panel.querySelectorAll(SELECTORS.section);
        sections.forEach((section) => closeSection(section));
    };

    /**
     * Open item
     */
    const openItem = (item) => {
        const trigger = item.querySelector(SELECTORS.trigger);
        const panel = item.nextElementSibling;

        if (!trigger || !panel || !panel.matches(SELECTORS.panel)) return;

        item.classList.add(CLASSES.open);
        trigger.setAttribute('aria-expanded', 'true');
        setPanelState(panel, true);

        updateItemPanelHeight(item);
    };

    /**
     * Setup each mega menu item
     */
    items.forEach((item, index) => {
        const trigger = item.querySelector(SELECTORS.trigger);
        const panel = item.nextElementSibling;

        if (!trigger) {
            console.warn(`Item ${index}: No trigger found`);

            return;
        }

        if (!panel || !panel.matches(SELECTORS.panel)) {
            console.warn(`Item ${index}: No panel found as next sibling`);

            return;
        }
        // Initialize panel state
        setPanelState(panel, false);

        // Trigger click handler
        trigger.addEventListener('click', (event) => {
            event.preventDefault();
            const isOpen = item.classList.contains(CLASSES.open);

            // Close other items
            items.forEach((other) => {
                if (other !== item) {
                    closeItem(other);
                }
            });

            // Toggle this item
            if (isOpen) {
                closeItem(item);
            } else {
                openItem(item);
            }
        });

        /**
         * Setup sections
         */
        const sections = Array.from(panel.querySelectorAll(SELECTORS.section));

        sections.forEach((section, sectionIndex) => {
            const sectionTrigger = section.querySelector(SELECTORS.sectionTrigger);
            const sectionPanel = section.querySelector(SELECTORS.sectionPanel);

            if (!sectionTrigger || !sectionPanel) {
                console.warn(`Item ${index}, Section ${sectionIndex}: Missing trigger or panel`);

                return;
            }

            // Initialize section state
            setPanelState(sectionPanel, false);

            // Section trigger click handler
            sectionTrigger.addEventListener('click', (event) => {
                event.preventDefault();

                const isOpen = section.classList.contains(CLASSES.open);

                // Close other sections
                sections.forEach((other) => {
                    if (other !== section) {
                        closeSection(other);
                    }
                });

                // Toggle this section
                if (isOpen) {
                    closeSection(section);
                } else {
                    openSection(section);
                }
            });
        });
    });

    /**
     * Update heights on window resize
     */
    const handleResize = () => {
        items.forEach((item) => {
            if (item.classList.contains(CLASSES.open)) {
                updateItemPanelHeight(item);

                const panel = item.nextElementSibling;
                if (panel && panel.matches(SELECTORS.panel)) {
                    const sections = panel.querySelectorAll(SELECTORS.section);
                    sections.forEach((section) => {
                        if (section.classList.contains(CLASSES.open)) {
                            const sectionPanel = section.querySelector(SELECTORS.sectionPanel);
                            if (sectionPanel) {
                                scheduleHeightUpdate(() => {
                                    setPanelHeight(sectionPanel, '--offcanvas-section-height');
                                });
                            }
                        }
                    });
                }
            }
        });
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
        window.removeEventListener('resize', handleResize);
    };
};
